import {routerActions} from 'actions/router-actions';
import {disposeSessionStore} from 'actions/session-actions';
import * as UsersApi from 'api/users-api';
import FormInput from 'components/form-elements/form-input';
import formManager from 'components/forms/form-manager';
import ErrorCodes from 'constants/error-codes';
import * as Immutable from 'immutable';
import messages from 'intl/anonymous-messages';
import globalMessages from 'intl/global-messages';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import React from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {bindActionCreators} from 'redux';
import {connect} from 'redux-store';
import * as ApiUtils from 'utils/api-utils';
import FormUtils from 'utils/form-utils';
import MeteorCookies from 'utils/meteor-cookies';
import * as PasswordUtils from 'utils/password-utils';
import S from './invalid-password.less';

import type {FormManagerProps} from 'components/forms/utils/form-manager-types';
import type {ErrorCode} from 'constants/error-codes';
import type {MessageDescriptor, WrappedComponentProps} from 'react-intl';
import type {ConnectedProps} from 'react-redux';

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            disposeSessionStore,
            replace: routerActions.replace,
        },
        dispatch,
    );
}

const connector = connect(
    (state) => ({
        loggedInUser: state.session.user,
        settings: state.session.settings,
    }),
    mapDispatchToProps,
    null,
    {forwardRef: true},
);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WrappedComponentProps & FormManagerProps & PropsFromRedux;

type State = {
    newPassword: string;
    password: string | null;
    isPasswordResetSuccess: boolean;
    error: ErrorCode | null;
};

/**
 * Used when a user's password expires or when an admin forces them to reset it.
 * NOT used for user to reset their own password when they've forgotten it.
 */
class InvalidPassword extends React.Component<Props, State> {
    static defaultProps: Pick<Props, 'loggedInUser' | 'settings' | 'disposeSessionStore'> = {
        // @ts-expect-error TS2322: Type Map<unknown, unknown> is not assignable to type User
        loggedInUser: Immutable.Map(),
        // @ts-expect-error TS2322: Type Map<unknown, unknown> is not assignable to type
        settings: Immutable.Map(),
        disposeSessionStore: () => null,
    };

    state = {
        newPassword: '',
        password: null,
        isPasswordResetSuccess: false,
        error: null,
    };

    UNSAFE_componentWillMount() {
        const {replace} = this.props;
        if (isNil(MeteorCookies.getInstance().get('token'))) {
            replace('/');
        }
    }

    componentWillUnmount() {
        this.props.disposeSessionStore();
    }

    _onSave() {
        this.props.handleSubmit(this._update.bind(this))();
    }

    async _update(data) {
        const command = data.delete('confirmPassword');

        try {
            await UsersApi.updateProfilePassword(command);
            await UsersApi.logout();
            const cookies = MeteorCookies.getInstance();
            cookies.remove('token');
            cookies.remove('clientId');

            this.setState({
                isPasswordResetSuccess: true,
                error: null,
            });
        } catch (err) {
            this.setState({
                isPasswordResetSuccess: false,
                error: err.response.data,
            });
        }
    }

    currentPasswordStatus() {
        return noop;
    }

    passwordSuccess = async () => {
        try {
            this.props.replace('/login');
        } catch (err) {
            ApiUtils.handleUnknownApiError(err);
        }
    };

    cancel() {
        // if we want to take the user to last request link, use the following.
        // it will be safer to take the user back to login screen so that a new session token is generated.
        // const {
        //     location,
        //     replace
        // } = this.props;
        // let nextPath = '/';
        // if (!isUndefined(location.state) && !isNull(location.state)) {
        //     let {nextPathName} = location.state;
        //     if (!isUndefined(nextPathName)) {
        //         nextPath = nextPathName;
        //     }
        // }
        // replace(nextPath);

        this.props.replace('/login');
    }

    onNewPasswordChanged(data) {
        this.setState({
            newPassword: data,
        });
    }

    render() {
        const {loggedInUser, intl, settings} = this.props;
        const {newPassword, password, isPasswordResetSuccess, error} = this.state;
        const {formatMessage} = intl;
        const passwordChangeRequired = loggedInUser.get('passwordChangeRequired');
        const passwordStrength = settings.get('passwordStrength', 1);

        if (isPasswordResetSuccess) {
            return (
                <div className={S.main}>
                    <div className={S.container}>
                        <div className={S.inner}>
                            <p className={S.resetSuccess}>
                                <FormattedMessage
                                    {...messages.resetSuccessDescriptionWithLink}
                                    values={{
                                        link: (
                                            <span onClick={this.passwordSuccess} className={S.returnToLoginLink}>
                                                <FormattedMessage {...messages.clickHere} />
                                            </span>
                                        ),
                                    }}
                                />
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className={S.main}>
                <form className={S.container} noValidate onSubmit={FormUtils.preventSubmit}>
                    <p className={S.helpText}>
                        {passwordChangeRequired ? (
                            <FormattedMessage {...messages.expiredResetTitle} />
                        ) : (
                            <FormattedMessage {...messages.expiredTitle} />
                        )}
                        <span>. </span>
                        <FormattedMessage {...messages.expiredHelpText} />
                    </p>
                    <div className={S.inner}>
                        {!isNil(error) && <PasswordError error={error} />}
                        <FormInput
                            label={formatMessage(globalMessages.oldPassword)}
                            helpText={formatMessage(globalMessages.oldPasswordHelp)}
                            name='password'
                            required={true}
                            autoFocus={true}
                            defaultValue=''
                            onChange={this.currentPasswordStatus.bind(this)}
                            type='password'
                        />
                        <FormInput
                            label={formatMessage(globalMessages.newPassword)}
                            helpText={formatMessage(PasswordUtils.getHelpTextMessage(passwordStrength))}
                            name='newPassword'
                            required={true}
                            defaultValue=''
                            type='password'
                            onChange={this.onNewPasswordChanged.bind(this)}
                            validator={PasswordUtils.validateNewPassword.bind(
                                this,
                                passwordStrength,
                                password,
                                newPassword,
                            )}
                        />
                        <FormInput
                            label={formatMessage(globalMessages.confirmPassword)}
                            helpText={formatMessage(globalMessages.confirmPasswordHelp)}
                            name='confirmPassword'
                            required={true}
                            defaultValue=''
                            type='password'
                            validator={PasswordUtils.validateConfirmPassword.bind(this, newPassword)}
                        />
                        <div className={S.controls}>
                            <button type='button' className={S.saveButton} onClick={this._onSave.bind(this)}>
                                <div className={S.buttonContent}>
                                    <i className={S.buttonIcon}>save</i>
                                    <div>
                                        <FormattedMessage {...globalMessages.save} />
                                    </div>
                                </div>
                            </button>
                            {!isNil(error) && (
                                <button type='button' className={S.cancelButton} onClick={this.cancel.bind(this)}>
                                    <div className={S.buttonContent}>
                                        <i className={S.buttonIcon}>arrow_back</i>
                                        <div>
                                            <FormattedMessage {...messages.returnToLogin} />
                                        </div>
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        );
    }
}

const PasswordError = ({error}: Pick<State, 'error'>) => {
    let errorMessageObj: MessageDescriptor;

    if (error.errorCode === ErrorCodes.REPEAT_PASSWORD.errorCode) {
        errorMessageObj = globalMessages.oldMatch;
    } else if (error.errorCode === ErrorCodes.MISMATCH_PASSWORD.errorCode) {
        errorMessageObj = globalMessages.incorrect;
    } else {
        errorMessageObj = messages.resetPasswordFailed;
    }
    return (
        <p className={S.errorText}>
            <FormattedMessage {...errorMessageObj} />
        </p>
    );
};

export default injectIntl(formManager()(connector(InvalidPassword)));
